import { Loader } from '@repo/go-ui';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReactZoomPanPinchHandlers } from 'react-zoom-pan-pinch';

import { useService } from 'src/packages/di';

import type { ImageBroadcastingWidgetState } from '../../model/ImageBroadcastingWidgetState';
import { getDateFnsLocale } from '../utils';

import { BroadcastImage } from './BroadcastImage';
import styles from './BroadcastPane.module.scss';

type BroadcastPaneProps = {
  widgetState: ImageBroadcastingWidgetState;
};

export const BroadcastPane = observer(
  // eslint-disable-next-line mobx/no-anonymous-observer
  forwardRef<ReactZoomPanPinchHandlers, BroadcastPaneProps>(function BroadcastPane({ widgetState }, ref) {
    const { t } = useTranslation('imageBroadcast');
    const language = useService('language');
    const currentImageUrl = widgetState.currentImageUrl;

    if (!currentImageUrl) {
      return null;
    }

    return (
      <>
        <div className={styles.BroadcastPane}>
          <div className={styles.TranslationPanelContainer}>
            {widgetState.selectedBroadcast &&
            widgetState.selectedBroadcast.info &&
            !widgetState.selectedBroadcast.inProgress ? (
              <div className={styles.TranslationPanelTextRow}>
                <span style={{ opacity: 0.6 }}>{t('updatedTime')}:</span>
                <span>{format(parseISO(widgetState.selectedBroadcast.info.updateTime), 'dd.MM.yyyy HH:mm')}</span>
                <span>
                  (
                  {formatDistanceToNow(parseISO(widgetState.selectedBroadcast.info.updateTime), {
                    locale: getDateFnsLocale(language.language),
                  })}
                  )
                </span>
              </div>
            ) : (
              <div className={styles.TranslationPanelPlaceholder} />
            )}
          </div>
          <div className={styles.BroadcastImageContainer}>
            <BroadcastImage
              key={currentImageUrl.url}
              src={currentImageUrl.url}
              alt={currentImageUrl.alt}
              screencastTooltips={widgetState.selectedBroadcast?.screencastTooltips}
              ref={ref}
              isTooltipVisible={
                widgetState.isTooltipVisible && !widgetState.appParams.resizing && !widgetState.appParams.moving
              }
            />
          </div>
        </div>
        {widgetState.selectedBroadcast && widgetState.selectedBroadcast.inProgress && (
          <Loader size="l" style={{ backgroundColor: 'transparent' }} />
        )}
      </>
    );
  }),
);
